import { useQuery } from '@tanstack/react-query';
import { first, isEmpty } from 'lodash-es';
import { useMemo } from 'react';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useGetBuyerPaymentMethods } from 'src/hooks/api/useGetBuyerPaymentMethods';
import { useGetEventFullInfo } from 'src/hooks/useGetEventFullInfo';
import { useGetUserInfos } from 'src/hooks/userGetUserInfo';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ListingClient,
  ListingDetailDataField,
  Marketplace,
  SaleDetails,
} from 'src/WebApiController';

export const useSaleInvoiceCatalogData = (sale: SaleDetails) => {
  const { listing: saleListing } = useGetSaleListing(sale);
  const { event } = useActivePosEntityContext();
  const { performer, venue } = useGetEventFullInfo(event);

  const isRetailSale = useMemo(
    () => sale.mkp === Marketplace.Retail,
    [sale.mkp]
  );

  const buyerEmail = sale.transDest?.email;
  const { data: buyerPaymentMethods } = useGetBuyerPaymentMethods(buyerEmail);

  const eligibleToDownload = useMemo(() => {
    if (!event) {
      return false;
    }
    if (isRetailSale) {
      return true; // Retail sales don't need tickets/ticketGroupInfos
    }
    if (isEmpty(sale.tickets) || isEmpty(sale.ticketGroupInfos)) {
      return false;
    }
    return true;
  }, [event, isRetailSale, sale.ticketGroupInfos, sale.tickets]);

  const buyerUserId = useMemo(() => {
    const ownerBuyerId = first(sale.owners?.buyerIds ?? []);
    if (ownerBuyerId) {
      return ownerBuyerId;
    }
    const tgBuyerId = first(sale.ticketGroupInfos.map((tg) => tg.buyerUserId));
    if (tgBuyerId) {
      return tgBuyerId;
    }
    return sale.insertBy;
  }, [sale.insertBy, sale.owners?.buyerIds, sale.ticketGroupInfos]);

  const useGetUserInfosQuery = useGetUserInfos(
    buyerUserId ? [buyerUserId] : undefined
  );

  const buyer = useMemo(() => {
    return buyerUserId ? useGetUserInfosQuery.data?.[buyerUserId] : undefined;
  }, [buyerUserId, useGetUserInfosQuery.data]);

  return {
    event,
    performer,
    venue,
    buyer,
    buyerPaymentMethods,
    eligibleToDownload,
    saleListing,
    isRetailSale,
  };
};

const useGetSaleListing = (sale: SaleDetails) => {
  const { activeAccountWebClientConfig } = useAppContext();

  const getListingQuery = useQuery({
    queryKey: [
      'ListingClient.GetListingByListingId',
      activeAccountWebClientConfig.activeAccountId,
      sale.listingId,
    ],
    queryFn: () => {
      if (
        activeAccountWebClientConfig.activeAccountId == null ||
        sale.listingId == null
      ) {
        return null;
      }
      return tryInvokeApi(() => {
        return new ListingClient(
          activeAccountWebClientConfig
        ).getListingByListingId(sale.listingId!, undefined, [
          ListingDetailDataField.Basic,
          ListingDetailDataField.Tickets,
        ]);
      });
    },
    enabled: sale.listingId != null,
    refetchOnWindowFocus: false,
  });

  return {
    listing: getListingQuery.data,
  };
};
